/**
 * Dashboard data fan-out — fetches the real KPI / module / activity sources for
 * the admin home (`DashboardHome`) in parallel, each gated by the viewer's
 * permission and degrading silently on 403 / network error (mirrors the
 * WhatsApp-cost card's degrade-on-error pattern). No backend changes: every
 * call reuses an already-shipped admin endpoint.
 *
 * Sources:
 *   - F45 analytics dashboard  (analytics:read)  → active users, bookings, trends
 *   - F44 finance summary      (financial:read)  → net revenue + trend
 *   - F61 page KPIs (dashboard)                  → venue total / active / flagged
 *                                                  (venues:read) + active users
 *                                                  (users:read), one unaudited read
 *                                                  (was two audited list reads)
 *   - F06 admin audit log      (audit:read)      → recent admin activity feed
 */
import { useEffect, useState } from 'react';
import type { AnalyticsDashboard, FinanceSummary, Permission } from '@bravemove/types';
import { adminAnalyticsApi } from '../api/analytics';
import { adminFinancialApi } from '../api/financial';
import { adminPageKpisApi } from '../api/pageKpis';
import { adminFetch, buildQuery } from '../auth/apiClient';
import type { AdminAuditEntry, ListEnvelope } from '../auth/types';

const WINDOW_DAYS = 30;
const ACTIVITY_LIMIT = 8;

export interface VenueCounts {
  readonly total: number;
  readonly active: number;
  readonly flagged: number;
}

export interface UserCounts {
  readonly total: number;
}

export interface DashboardData {
  readonly analytics: AnalyticsDashboard | null;
  readonly finance: FinanceSummary | null;
  readonly venues: VenueCounts | null;
  readonly users: UserCounts | null;
  readonly activity: readonly AdminAuditEntry[];
  readonly loading: boolean;
}

const EMPTY: DashboardData = {
  analytics: null,
  finance: null,
  venues: null,
  users: null,
  activity: [],
  loading: true,
};

function isoDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

export function useDashboardData(permissions: readonly Permission[]): DashboardData {
  const [data, setData] = useState<DashboardData>(EMPTY);

  const canAnalytics = permissions.includes('analytics:read');
  const canFinance = permissions.includes('financial:read');
  const canVenues = permissions.includes('venues:read');
  const canUsers = permissions.includes('users:read');
  const canAudit = permissions.includes('audit:read');

  useEffect(() => {
    let cancelled = false;

    const to = isoDay(new Date());
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - WINDOW_DAYS);
    const from = isoDay(fromDate);

    const load = async (): Promise<void> => {
      const [analytics, finance, kpis, activity] = await Promise.all([
        canAnalytics
          ? adminAnalyticsApi.getDashboard({ from, to, compare: true }).catch(() => null)
          : Promise.resolve(null),
        canFinance
          ? adminFinancialApi.getSummary({ from, to, compare: true }).catch(() => null)
          : Promise.resolve(null),
        canVenues || canUsers
          ? adminPageKpisApi.get('dashboard').catch(() => null)
          : Promise.resolve(null),
        canAudit
          ? adminFetch<ListEnvelope<AdminAuditEntry>>(
              `/admin/audit-log${buildQuery({ limit: ACTIVITY_LIMIT, offset: 0 })}`,
            )
              .then((b): readonly AdminAuditEntry[] => b.items)
              .catch(() => [] as readonly AdminAuditEntry[])
          : Promise.resolve([] as readonly AdminAuditEntry[]),
      ]);
      if (cancelled) return;
      const venues: VenueCounts | null = kpis?.venues ?? null;
      const users: UserCounts | null =
        kpis?.activeUsers !== undefined ? { total: kpis.activeUsers } : null;
      setData({ analytics, finance, venues, users, activity, loading: false });
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [canAnalytics, canFinance, canVenues, canUsers, canAudit]);

  return data;
}
